import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiUrl } from "../utils/apiBase";
import { useScrollToHash } from "../utils/use-scroll-to-hash";
import { pluralPL } from "../utils/pluralPL";
import { cleanTournamentName } from "../utils/cleanTournamentName";
import { roundCta } from "./round-cta";

interface LeagueMemberItem {
  id: number;
  player?: { id: number; displayName: string };
  groupName?: string | null;
  withdrawn: boolean;
  joinSource?: string | null;
  confirmedAt?: string | null;
}

interface LeagueRoundItem {
  id: number;
  name: string;
  number: number;
  status: string;
  seasonId: number | null;
  startDate: string | null;
  startTime?: string | null;
}

interface LeagueSeasonItem {
  id: number;
  name: string;
  number: number;
  status: string;
  startDate: string | null;
  endDate: string | null;
}

interface LeagueDetail {
  id: number;
  name: string;
  discipline: string;
  playMode?: string | null;
  startDate: string;
  endDate: string;
  status: string;
  useGroups: boolean;
  signupMode?: "PRIVATE" | "OPEN" | "APPROVAL" | "APPROVAL_EXCEPT_MEMBERS";
  venue?: string | null;
  venueAddress?: string | null;
  venueCity?: string | null;
  defaultStartTime?: string | null;
  club?: { slug: string; name: string } | null;
  members: LeagueMemberItem[];
  rounds: LeagueRoundItem[];
  seasons: LeagueSeasonItem[];
}

interface MemberBadge {
  label: string;
  tone: "confirmed" | "auto" | "pending";
  title: string;
}

function memberJoinBadge(m: LeagueMemberItem): MemberBadge {
  if (!m.joinSource || m.joinSource === "LEGACY") {
    return {
      label: "Aktywny",
      tone: "auto",
      title: "Członek z poprzednich sezonów",
    };
  }

  if (m.joinSource === "ADMIN_ADDED") {
    return { label: "Auto", tone: "auto", title: "Dodany przez organizatora" };
  }

  if (m.confirmedAt) {
    return {
      label: "Potwierdzony",
      tone: "confirmed",
      title: "Zatwierdzony przez organizatora",
    };
  }

  if (m.joinSource === "LIST_PICK" || m.joinSource === "CODE") {
    return {
      label: "Potwierdzony",
      tone: "confirmed",
      title: "Dołączył bez wymaganej akceptacji",
    };
  }

  return {
    label: "Oczekuje",
    tone: "pending",
    title: "Czeka na akceptację organizatora",
  };
}

interface StandingEntry {
  entityId: number;
  name: string;
  groupName?: string;
  wins: number;
  losses: number;
  walkovers: number;
  points: number;
  matchesPlayed: number;
  setsWon: number;
  setsLost: number;
}

interface SeasonRankingEntry {
  entityId: number;
  rank?: number;
  name: string;
  playerIds: number[];
  playerRatings?: (number | null)[];
  groupName: string | null;
  roundsPlayed: number;
  countedRounds: number;
  countedPoints: number;
  totalPoints: number;
  perRoundPoints: number[];
}

interface SeasonRankingMeta {
  roundsTotal: number;
  roundsPlayed: number;
  seasonTarget: number;
  scoringRule: string;
  // Null = sezon ma własną liczbę kolejek, nie procent.
  countedPercent?: number | null;
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    PLANNED: "Planowana",
    ACTIVE: "Aktywna",
    OPEN: "Otwarta",
    CLOSED: "Zamknięta",
    FINISHED: "Zakończona",
    DRAFT: "Szkic",
    ARCHIVED: "Archiwum",
  };

  return labels[status] ?? status;
}

// Americano is stored as a singles league so signups stay individual; the badge
// has to say what is actually played, or it promises 1 vs 1.
function playLabel(discipline: string, playMode?: string | null): string {
  if (playMode === "AMERICANO") {
    return "Americano — debel 2 na 2, zapisy indywidualne";
  }

  return disciplineLabel(discipline);
}

function disciplineLabel(discipline: string): string {
  const labels: Record<string, string> = {
    SINGLES_M: "Singiel M",
    SINGLES_F: "Singiel K",
    SINGLES_MIX: "Singiel Mix",
    DOUBLES_M: "Debel M",
    DOUBLES_F: "Debel K",
    DOUBLES_MIX: "Debel Mix",
  };

  return labels[discipline] ?? discipline;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function memberDisplayName(member: LeagueMemberItem): string {
  if (member.player) {
    return member.player.displayName;
  }

  return `Uczestnik ${member.id}`;
}

// Aktualny sezon: pierwszy ACTIVE, fallback do najnowszego (po number desc).
function pickCurrentSeason(
  seasons: LeagueSeasonItem[],
): LeagueSeasonItem | null {
  if (seasons.length === 0) {
    return null;
  }

  const active = seasons.find((s) => s.status === "ACTIVE");
  if (active) {
    return active;
  }

  return [...seasons].sort((a, b) => b.number - a.number)[0];
}

const LeagueDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [league, setLeague] = useState<LeagueDetail | null>(null);
  const [seasonRanking, setSeasonRanking] = useState<SeasonRankingEntry[]>([]);
  const [rankingMeta, setRankingMeta] = useState<SeasonRankingMeta | null>(
    null,
  );
  const [rankingLoading, setRankingLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      return;
    }

    fetch(apiUrl(`/api/public/leagues/${id}`))
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        return res.json() as Promise<LeagueDetail>;
      })
      .then((leagueData) => {
        setLeague(leagueData);
        setLoading(false);
      })
      .catch(() => {
        setError("Nie udało się załadować ligi.");
        setLoading(false);
      });
  }, [id]);

  const currentSeasonForRanking = league
    ? pickCurrentSeason(league.seasons ?? [])
    : null;
  const currentSeasonId = currentSeasonForRanking?.id ?? null;

  // Ranking aktualnego sezonu — tylko bieżący sezon na tej stronie.
  // Pełny widok z selektorem sezonu jest na /standings.
  // Czekamy aż `league` się załaduje — inaczej pierwszy strzał idzie bez
  // seasonId i agreguje all-time (np. 640 kolejek dla LABA), a jeśli wróci
  // później niż strzał po S{n}, nadpisuje poprawny ranking sezonu.
  useEffect(() => {
    if (!id || !league) {
      return;
    }

    setRankingLoading(true);
    const controller = new AbortController();
    const seasonParam =
      currentSeasonId !== null ? `?seasonId=${currentSeasonId}` : "";
    fetch(apiUrl(`/api/public/leagues/${id}/season-ranking${seasonParam}`), {
      signal: controller.signal,
    })
      .then((res) =>
        res.ok
          ? (res.json() as Promise<{
              ranking: SeasonRankingEntry[];
              meta?: SeasonRankingMeta;
            }>)
          : { ranking: [], meta: undefined },
      )
      .then((data) => {
        setSeasonRanking(data.ranking ?? []);
        setRankingMeta(data.meta ?? null);
      })
      .catch((err) => {
        if (err?.name === "AbortError") {
          return;
        }

        setSeasonRanking([]);
        setRankingMeta(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setRankingLoading(false);
        }
      });

    return () => controller.abort();
  }, [id, league, currentSeasonId]);

  useScrollToHash(!loading && !error);

  if (loading) {
    return <div className="event-detail-loading">Ładowanie...</div>;
  }

  if (error || !league) {
    return (
      <div className="event-detail-error">
        {error ?? "Nie znaleziono ligi."}
      </div>
    );
  }

  const activeMembers = league.members.filter((m) => !m.withdrawn);
  const currentSeason = currentSeasonForRanking;

  return (
    <div className="event-detail event-detail--league">
      <Link to="/leagues" className="event-detail-back">
        ← Powrót do lig
      </Link>
      <h1 className="event-detail-name">{cleanTournamentName(league.name)}</h1>
      <div className="event-detail-meta">
        <div>
          <strong>Dyscyplina:</strong> {playLabel(league.discipline, league.playMode)}
        </div>
        <div>
          <strong>Status:</strong>{" "}
          <span
            className={`league-status-badge league-status-badge--${league.status.toLowerCase()}`}
          >
            {statusLabel(league.status)}
          </span>
        </div>
        {league.club && league.club.slug !== "global" && (
          <div>
            <strong>Organizator:</strong>{" "}
            <Link to={`/clubs/${league.club.slug}`} className="player-link">
              {league.club.name}
            </Link>
          </div>
        )}
        {league.venue && (
          <div>
            <strong>Miejsce:</strong> {league.venue}
            {league.venueCity ? ` · ${league.venueCity}` : ""}
            {league.venueAddress && (
              <span className="event-detail-meta__sub">
                {" "}
                ({league.venueAddress})
              </span>
            )}
          </div>
        )}
        {league.defaultStartTime && (
          <div>
            <strong>Godzina kolejek:</strong> {league.defaultStartTime}
          </div>
        )}
      </div>

      {(() => {
        // Aktualny sezon = pickCurrentSeason. Hero-panel: lewy sidebar S{n}
        // (mocniejszy niż archive), prawy body — statystyki, ewentualna aktywna
        // kolejka (jeśli OPEN/REGISTRATION/ACTIVE), siatka WSZYSTKICH kolejek
        // sezonu (zawsze widoczna), tabela top-5 + link do pełnej. Inne sezony
        // w archiwum niżej.
        const seasonRoundsAll = currentSeason
          ? league.rounds.filter((r) => r.seasonId === currentSeason.id)
          : league.rounds;
        const upcoming = seasonRoundsAll
          .filter((r) => r.status !== "CLOSED")
          .slice()
          .sort((a, b) => {
            const aDate = a.startDate
              ? new Date(a.startDate).getTime()
              : Number.POSITIVE_INFINITY;
            const bDate = b.startDate
              ? new Date(b.startDate).getTime()
              : Number.POSITIVE_INFINITY;
            if (aDate !== bDate) {
              return aDate - bDate;
            }

            return a.number - b.number;
          });
        const primaryRound = upcoming[0] ?? null;
        const closedThisSeason = seasonRoundsAll.filter(
          (r) => r.status === "CLOSED",
        ).length;
        const pastRounds = seasonRoundsAll
          .filter((r) => r.status === "CLOSED")
          .slice()
          .sort((a, b) => b.number - a.number);

        if (
          !primaryRound &&
          seasonRanking.length === 0 &&
          !rankingLoading &&
          seasonRoundsAll.length === 0
        ) {
          return null;
        }

        const seasonTitle = currentSeason
          ? currentSeason.name
          : cleanTournamentName(league.name);
        const seasonDateMeta =
          currentSeason && currentSeason.startDate && currentSeason.endDate
            ? `${formatDate(currentSeason.startDate)} – ${formatDate(currentSeason.endDate)}`
            : null;
        const seasonIndexLabel = currentSeason
          ? `S${String(currentSeason.number).padStart(2, "0")}`
          : "·";

        return (
          <section className="current-season" id="zapisy">
            <div className="current-season__shell">
              <aside className="current-season__rail">
                <span className="current-season__rail-eyebrow">
                  Sezon aktualny
                </span>
                <span className="current-season__rail-index" aria-hidden="true">
                  {seasonIndexLabel}
                </span>
                <span className="current-season__rail-badge">Live</span>
              </aside>

              <div className="current-season__main">
                <header className="current-season__header">
                  <h2 className="current-season__title">{seasonTitle}</h2>
                  <div className="current-season__sub">
                    {seasonDateMeta && (
                      <span className="current-season__meta">
                        {seasonDateMeta}
                      </span>
                    )}
                  </div>
                </header>

                {(closedThisSeason > 0 || seasonRanking.length > 0) && (
                  <dl className="current-season__stats">
                    {seasonRoundsAll.length > 0 && (
                      <div className="current-season__stat">
                        <dt>Kolejki</dt>
                        <dd>
                          <strong>{closedThisSeason}</strong>
                          <span className="current-season__stat-of">
                            {" "}
                            / {seasonRoundsAll.length}
                          </span>
                        </dd>
                      </div>
                    )}
                    {seasonRanking.length > 0 && (
                      <div className="current-season__stat">
                        <dt>Zawodnicy</dt>
                        <dd>
                          <strong>{seasonRanking.length}</strong>
                        </dd>
                      </div>
                    )}
                  </dl>
                )}

                {primaryRound && (
                  <div className="current-season__active">
                    {(() => {
                      const round = primaryRound;
                      const dateText = round.startDate
                        ? formatDate(round.startDate)
                        : null;
                      const timeText =
                        round.startTime ?? league.defaultStartTime ?? null;
                      const statusText =
                        round.status === "REGISTRATION"
                          ? "Zapisy otwarte"
                          : round.status === "GROUPING"
                            ? "Losowanie grup"
                            : round.status === "ACTIVE"
                              ? "W toku"
                              : round.status === "DRAFT"
                                ? "Wkrótce"
                                : round.status === "CANCELLED"
                                  ? "Odwołana"
                                  : "Oczekuje";
                      const isLive = round.status === "ACTIVE";
                      const { label: ctaText, href: ctaHref } = roundCta(round, league.id);

                      return (
                        <>
                          <div className="current-season__active-flag">
                            {isLive ? "Trwa teraz" : "Najbliższa kolejka"}
                          </div>
                          <div className="current-season__active-body">
                            <div className="current-season__active-meta">
                              {dateText && (
                                <time className="current-season__active-date">
                                  {dateText}
                                </time>
                              )}
                              {!dateText && timeText && (
                                <time className="current-season__active-date">
                                  godz. {timeText}
                                </time>
                              )}
                              <span className="current-season__active-status">
                                {statusText}
                              </span>
                            </div>
                            <h3 className="current-season__active-name">
                              {round.name}
                            </h3>
                            {(timeText || league.venue) && (
                              <div className="current-season__active-hint">
                                {timeText && dateText && <>🕒 {timeText}</>}
                                {timeText && dateText && league.venue && (
                                  <> · </>
                                )}
                                {league.venue && <>📍 {league.venue}</>}
                                {league.venueCity && <> · {league.venueCity}</>}
                              </div>
                            )}
                          </div>
                          <Link
                            to={ctaHref}
                            className="current-season__active-cta"
                          >
                            {ctaText}
                          </Link>
                        </>
                      );
                    })()}
                  </div>
                )}

                {pastRounds.length > 0 && (
                  <details className="current-season__past">
                    <summary className="current-season__past-toggle">
                      <span>Pokaż rozegrane kolejki ({pastRounds.length})</span>
                      <span
                        className="current-season__past-glyph"
                        aria-hidden="true"
                      >
                        ▾
                      </span>
                    </summary>
                    <ul className="current-season__rounds-grid">
                      {pastRounds.map((round) => (
                        <li
                          key={round.id}
                          className={`round-chip round-chip--${round.status.toLowerCase()}`}
                        >
                          <Link
                            to={`/leagues/${league.id}/rounds/${round.id}`}
                            className="round-chip__link"
                          >
                            <span className="round-chip__num">
                              K{String(round.number).padStart(2, "0")}
                            </span>
                            <span className="round-chip__name">
                              {round.name}
                            </span>
                            <span className="round-chip__status">
                              zakończona
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                <div className="current-season__standings">
                  <div className="current-season__standings-head">
                    <h3 className="current-season__standings-title">
                      Tabela sezonu
                    </h3>
                    <span className="current-season__standings-sub">Top 5</span>
                  </div>

                  {rankingLoading ? (
                    <div className="current-season__loading">
                      Ładowanie tabeli…
                    </div>
                  ) : seasonRanking.length === 0 ? (
                    <p className="current-season__empty">
                      Sezon jeszcze nie ruszył — brak rozegranych kolejek do
                      tabeli.
                    </p>
                  ) : (
                    <>
                      <div className="league-standings-table-wrap">
                        <table className="league-standings-table">
                          <thead>
                            <tr>
                              <th className="league-standings-table__th league-standings-table__th--left">
                                #
                              </th>
                              <th className="league-standings-table__th league-standings-table__th--left">
                                Gracz
                              </th>
                              <th
                                className="league-standings-table__th"
                                title={
                                  rankingMeta
                                    ? rankingMeta.countedPercent === null
                                      ? `Suma punktów z max ${rankingMeta.seasonTarget} najlepszych z ${rankingMeta.roundsTotal} kolejek`
                                      : `Suma punktów z max ${rankingMeta.seasonTarget} najlepszych kolejek (${rankingMeta.countedPercent ?? 77}% z ${rankingMeta.roundsTotal})`
                                    : "Suma punktów z najlepszych kolejek"
                                }
                              >
                                Pkt
                              </th>
                              <th
                                className="league-standings-table__th league-standings-table__th--muted"
                                title="Ile kolejek gracz rozegrał"
                              >
                                Grane
                              </th>
                              <th
                                className="league-standings-table__th league-standings-table__th--muted"
                                title="Ile kolejek liczy się do Pkt"
                              >
                                Liczone
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...seasonRanking]
                              .sort((a, b) => {
                                if (b.countedPoints !== a.countedPoints) {
                                  return b.countedPoints - a.countedPoints;
                                }

                                if (b.totalPoints !== a.totalPoints) {
                                  return b.totalPoints - a.totalPoints;
                                }

                                return a.name.localeCompare(b.name);
                              })
                              .slice(0, 5)
                              .map((entry, index) => {
                              const allCounted =
                                entry.countedRounds === entry.roundsPlayed;
                              const droppedCount =
                                entry.roundsPlayed - entry.countedRounds;
                              const pktTooltip = allCounted
                                ? `Wszystkie ${entry.roundsPlayed} kolejek wlicza się do Pkt (suma: ${entry.perRoundPoints.join(" + ")}).`
                                : `Liczone ${entry.countedRounds} z ${entry.roundsPlayed}: ${entry.perRoundPoints.slice(0, entry.countedRounds).join(" + ")}. Dropowane ${droppedCount} najsłabszych.`;

                              return (
                                <tr
                                  key={entry.entityId}
                                  className={`league-standings-table__row${index % 2 !== 0 ? " league-standings-table__row--alt" : ""}`}
                                >
                                  <td className="league-standings-table__td league-standings-table__td--muted">
                                    {entry.rank ?? index + 1}
                                  </td>
                                  <td className="league-standings-table__td">
                                    <EntityNameWithRatings entry={entry} />
                                  </td>
                                  <td
                                    className="league-standings-table__td league-standings-table__td--center league-standings-table__td--bold"
                                    title={pktTooltip}
                                  >
                                    {entry.countedPoints}
                                  </td>
                                  <td className="league-standings-table__td league-standings-table__td--center league-standings-table__td--muted">
                                    {rankingMeta ? (
                                      <>
                                        {entry.roundsPlayed}{" "}
                                        <span style={{ opacity: 0.5 }}>
                                          z {rankingMeta.roundsTotal}
                                        </span>
                                      </>
                                    ) : (
                                      entry.roundsPlayed
                                    )}
                                  </td>
                                  <td className="league-standings-table__td league-standings-table__td--center league-standings-table__td--muted">
                                    <strong>{entry.countedRounds}</strong>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="current-season__standings-foot">
                        <Link
                          to={`/leagues/${league.id}/standings${currentSeasonId !== null ? `?seasonId=${currentSeasonId}` : ""}`}
                          className="current-season__standings-link"
                        >
                          Pełna tabela
                          {seasonRanking.length > 5
                            ? ` (${seasonRanking.length})`
                            : ""}{" "}
                          →
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {(() => {
        if (league.rounds.length === 0) {
          return null;
        }

        // Group rounds by seasonId — most leagues have multiple seasons spanning
        // years. Each season collapses behind its name; orphan rounds (no
        // seasonId) land in their own "Inne kolejki" bucket.
        const seasonsById = new Map<number, LeagueSeasonItem>();
        for (const s of league.seasons ?? []) {
          seasonsById.set(s.id, s);
        }
        const groupedRounds = new Map<number | "orphan", LeagueRoundItem[]>();
        for (const round of league.rounds) {
          const key: number | "orphan" = round.seasonId ?? "orphan";
          if (!groupedRounds.has(key)) {
            groupedRounds.set(key, []);
          }
          groupedRounds.get(key)!.push(round);
        }

        // Order: known seasons by their `number`, then orphan rounds.
        // Pomijamy aktualny sezon — jest już w panelu „Sezon aktualny" wyżej.
        const orderedSeasonIds = (league.seasons ?? [])
          .filter((s) => groupedRounds.has(s.id))
          .filter((s) => s.id !== currentSeasonId)
          .sort((a, b) => b.number - a.number)
          .map((s) => s.id);
        const allKeys: Array<number | "orphan"> = [...orderedSeasonIds];
        if (groupedRounds.has("orphan")) {
          allKeys.push("orphan");
        }

        if (allKeys.length === 0) {
          return null;
        }

        return (
          <section className="archive">
            <header className="archive__header">
              <span className="eyebrow archive__eyebrow">Archiwum</span>
              <h2 className="archive__heading">Sezony i kolejki</h2>
              <p className="archive__hint">
                Pełna historia ligi. Kliknij sezon, aby zobaczyć ranking i
                wszystkie kolejki.
              </p>
            </header>
            <ol className="archive__list">
              {allKeys.map((key) => {
                const rounds = groupedRounds.get(key) ?? [];
                const season =
                  typeof key === "number" ? seasonsById.get(key) : null;
                const title = season ? season.name : "Bez sezonu";
                const closedCount = rounds.filter(
                  (r) => r.status === "CLOSED",
                ).length;
                const meta =
                  season && season.startDate && season.endDate
                    ? `${formatDate(season.startDate)} – ${formatDate(season.endDate)}`
                    : null;
                const seasonHref =
                  typeof key === "number"
                    ? `/leagues/${league.id}/standings?seasonId=${key}`
                    : `/leagues/${league.id}/standings`;
                const seasonNumberLabel = season
                  ? `S${String(season.number).padStart(2, "0")}`
                  : "·";

                return (
                  <li key={String(key)} className="season-card">
                    <div className="season-card__sidebar">
                      <span className="season-card__index" aria-hidden="true">
                        {seasonNumberLabel}
                      </span>
                    </div>
                    <div className="season-card__body">
                      <div className="season-card__head">
                        <div className="season-card__head-text">
                          <h3 className="season-card__title">
                            <Link
                              to={seasonHref}
                              className="season-card__title-link"
                            >
                              {title}
                            </Link>
                          </h3>
                          {meta && <p className="season-card__meta">{meta}</p>}
                        </div>
                        <Link to={seasonHref} className="season-card__cta">
                          Zobacz tabelę sezonu →
                        </Link>
                      </div>

                      <dl className="season-card__stats">
                        <div className="season-card__stat">
                          <dt>Kolejki rozegrane</dt>
                          <dd>
                            <strong>{closedCount}</strong>
                            <span className="season-card__stat-of">
                              {" "}
                              / {rounds.length}
                            </span>
                          </dd>
                        </div>
                        <div className="season-card__stat season-card__stat--quiet">
                          <dt>Łącznie</dt>
                          <dd>
                            {rounds.length}{" "}
                            {pluralPL(
                              rounds.length,
                              "kolejka",
                              "kolejki",
                              "kolejek",
                            )}
                          </dd>
                        </div>
                      </dl>

                      <details className="season-card__rounds">
                        <summary className="season-card__rounds-toggle">
                          <span>Pokaż wszystkie kolejki</span>
                          <span
                            className="season-card__rounds-toggle-glyph"
                            aria-hidden="true"
                          >
                            ▾
                          </span>
                        </summary>
                        <ul className="season-card__round-grid">
                          {rounds
                            .slice()
                            .sort((a, b) => b.number - a.number)
                            .map((round) => {
                              const statusKey = round.status.toLowerCase();
                              const statusReadable =
                                round.status === "CLOSED"
                                  ? "zakończona"
                                  : round.status === "ACTIVE"
                                    ? "w toku"
                                    : round.status === "REGISTRATION"
                                      ? "zapisy otwarte"
                                      : round.status === "GROUPING"
                                        ? "losowanie grup"
                                        : round.status === "PENDING"
                                          ? "oczekuje"
                                          : round.status === "DRAFT"
                                            ? "planowana"
                                            : round.status === "CANCELLED"
                                              ? "odwołana"
                                              : statusKey;

                              return (
                                <li
                                  key={round.id}
                                  className={`round-chip round-chip--${statusKey}`}
                                >
                                  <Link
                                    to={`/leagues/${league.id}/rounds/${round.id}`}
                                    className="round-chip__link"
                                  >
                                    <span className="round-chip__num">
                                      K{String(round.number).padStart(2, "0")}
                                    </span>
                                    <span className="round-chip__name">
                                      {round.name}
                                    </span>
                                    <span className="round-chip__status">
                                      {statusReadable}
                                    </span>
                                  </Link>
                                </li>
                              );
                            })}
                        </ul>
                      </details>
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>
        );
      })()}

      {activeMembers.length > 0 && (
        <details className="league-members">
          <summary className="league-members__summary">
            <h2 className="league-members__heading">
              Członkowie ligi ({activeMembers.length})
            </h2>
            <span className="league-members__hint">
              Gracze którzy kiedykolwiek zagrali w tej lidze. Bycie tu nie
              oznacza zapisu na nadchodzącą kolejkę — na każdą zapisujesz się
              osobno.
            </span>
          </summary>
          <ul className="league-members-list">
            {activeMembers.map((member) => {
              const badge = memberJoinBadge(member);
              return (
                <li key={member.id} className="league-members-list__item">
                  <span>{memberDisplayName(member)}</span>
                  <span
                    className={`league-members-list__badge league-members-list__badge--${badge.tone}`}
                    title={badge.title}
                  >
                    {badge.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </div>
  );
};

const EntityNameWithRatings: React.FC<{ entry: SeasonRankingEntry }> = ({ entry }) => {
  const names = entry.name.split(" / ");
  const ids = entry.playerIds ?? [];
  const ratings = entry.playerRatings ?? [];
  const isPair = names.length > 1 && ids.length === names.length;
  const suffix = isPair ? "?tab=debel" : "";

  if (!isPair) {
    const id = ids[0];
    const rating = ratings[0] ?? null;
    const nameEl =
      typeof id === "number" ? (
        <Link to={`/player/${id}${suffix}`} className="player-link">
          {entry.name}
        </Link>
      ) : (
        <>{entry.name}</>
      );

    return (
      <span className="entity-name-rating">
        {nameEl}
        <RatingChip rating={rating} />
      </span>
    );
  }

  return (
    <span className="entity-name-rating">
      {names.map((n, i) => (
        <React.Fragment key={`${entry.entityId}-${ids[i]}`}>
          {i > 0 && <span className="entity-name-rating__sep"> / </span>}
          <Link to={`/player/${ids[i]}${suffix}`} className="player-link">
            {n}
          </Link>
          <RatingChip rating={ratings[i] ?? null} />
        </React.Fragment>
      ))}
    </span>
  );
};

const RatingChip: React.FC<{ rating: number | null }> = ({ rating }) => (
  <span
    className={`rating-inline-chip${rating == null ? " rating-inline-chip--empty" : ""}`}
    title={rating == null ? "Brak rankingu" : "Aktualny ranking"}
  >
    {rating == null ? "—" : rating}
  </span>
);

export default LeagueDetail;
